import React from "react";
import { ExternalLink, Users, MapPin, Globe2, Image as ImageIcon,
         PlayCircle, MessageCircle, Heart, BookmarkPlus, Eye } from "lucide-react";

/**
 * MetaBreakdowns — renders the enriched Facebook + Instagram breakdown
 * data captured by the backend Meta collector:
 *
 *   • top_facebook_posts   — 5 highest-engagement page posts (message,
 *     permalink, impressions, reach, engaged users, reactions, clicks)
 *   • top_instagram_media  — 5 highest-reach IG posts (caption, media
 *     type, thumbnail, likes, comments, saved, reach, impressions)
 *   • page_gender_age      — top 8 audience segments as a bar list
 *   • page_top_countries   — top 5 countries by fan count
 *   • page_top_cities      — top 5 cities by fan count
 *   • facebook_profile / instagram_profile  — profile identity cards
 *
 * Only renders sections that have data — empty breakdowns collapse
 * silently rather than showing empty placeholders.
 */
export function MetaBreakdowns({ breakdowns, testIdPrefix }) {
  const b = breakdowns || {};
  const fbPosts = b.top_facebook_posts || [];
  const igMedia = b.top_instagram_media || [];
  const genderAge = b.page_gender_age || [];
  const countries = b.page_top_countries || [];
  const cities = b.page_top_cities || [];
  const fbProfile = b.facebook_profile;
  const igProfile = b.instagram_profile;

  const anything = fbPosts.length || igMedia.length || genderAge.length
                   || countries.length || cities.length
                   || fbProfile || igProfile;
  if (!anything) return null;

  return (
    <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-meta-breakdowns`}>
      {(fbProfile || igProfile) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fbProfile && <ProfileCard label="Facebook Page" data={fbProfile}
            testId={`${testIdPrefix}-fb-profile`} accent="#1877F2" />}
          {igProfile && <ProfileCard label="Instagram" data={igProfile}
            testId={`${testIdPrefix}-ig-profile`} accent="#E1306C" />}
        </div>
      )}

      {!!fbPosts.length && (
        <TopPostsPanel title="Top Facebook posts" posts={fbPosts}
          testId={`${testIdPrefix}-fb-posts`} platform="facebook" />
      )}

      {!!igMedia.length && (
        <TopPostsPanel title="Top Instagram media" posts={igMedia}
          testId={`${testIdPrefix}-ig-media`} platform="instagram" />
      )}

      {(genderAge.length || countries.length || cities.length) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3"
          data-testid={`${testIdPrefix}-demographics`}>
          {!!genderAge.length && (
            <DemoBarList title="Audience by age & gender"
              rows={genderAge} valueKey="count" labelKey="segment"
              icon={Users} testId={`${testIdPrefix}-gender-age`} />
          )}
          {!!countries.length && (
            <DemoBarList title="Top countries"
              rows={countries} valueKey="fans" labelKey="country"
              icon={Globe2} testId={`${testIdPrefix}-countries`} />
          )}
          {!!cities.length && (
            <DemoBarList title="Top cities"
              rows={cities} valueKey="fans" labelKey="city"
              icon={MapPin} testId={`${testIdPrefix}-cities`} />
          )}
        </div>
      )}
    </div>
  );
}

function ProfileCard({ label, data, accent, testId }) {
  const link = data.link || data.website;
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-3
      flex gap-3 items-start" data-testid={testId}>
      {data.profile_picture_url ? (
        <img src={data.profile_picture_url} alt=""
          className="h-12 w-12 rounded-sm object-cover border border-zinc-200" />
      ) : (
        <div className="h-12 w-12 rounded-sm flex items-center justify-center"
          style={{ backgroundColor: accent + "20", color: accent }}>
          <ImageIcon className="h-5 w-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="text-[9px] font-mono uppercase tracking-wider"
            style={{ color: accent }}>{label}</div>
          {data.category && (
            <span className="text-[9px] font-mono text-zinc-500">
              · {data.category}
            </span>
          )}
        </div>
        <div className="font-display font-bold text-[13px] truncate">
          {data.name || data.username}
        </div>
        {data.username && data.name && (
          <div className="text-[10px] font-mono text-zinc-500">@{data.username}</div>
        )}
        {data.biography && (
          <div className="text-[11px] text-zinc-600 mt-1 line-clamp-2">
            {data.biography}
          </div>
        )}
        {data.about && (
          <div className="text-[11px] text-zinc-600 mt-1 line-clamp-2">
            {data.about}
          </div>
        )}
        {link && (
          <a href={link} target="_blank" rel="noreferrer"
            className="text-[10px] text-blue-600 hover:underline
              inline-flex items-center gap-0.5 mt-1">
            {link.replace(/^https?:\/\//i, "").slice(0, 50)}
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </div>
  );
}

function TopPostsPanel({ title, posts, testId, platform }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden"
      data-testid={testId}>
      <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-800">
          {title}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          Ranked by {platform === "instagram" ? "reach" : "engaged users"} in
          the reporting period.
        </div>
      </div>
      <div className="divide-y divide-zinc-100">
        {posts.map((p, i) => (
          <PostRow key={p.id || i} post={p} platform={platform}
            testId={`${testId}-post-${i}`} />
        ))}
      </div>
    </div>
  );
}

function PostRow({ post, platform, testId }) {
  const thumbnail = post.thumbnail_url || post.media_url;
  const permalink = post.permalink;
  const isVideo = (post.media_type || "").toUpperCase().includes("VIDEO")
                  || (post.media_type || "").toUpperCase() === "REELS"
                  || !!post.video_views;
  const text = post.caption || post.message || "(no text)";
  const date = post.timestamp || post.created_time;
  const stats = platform === "instagram"
    ? [
        { icon: Eye,           key: "reach",         label: "reach" },
        { icon: PlayCircle,    key: "impressions",   label: "impressions" },
        { icon: Heart,         key: "like_count",    label: "likes" },
        { icon: MessageCircle, key: "comments_count", label: "comments" },
        { icon: BookmarkPlus,  key: "saved",         label: "saved" },
      ]
    : [
        { icon: PlayCircle,    key: "impressions",   label: "impressions" },
        { icon: Eye,           key: "reach",         label: "reach" },
        { icon: Users,         key: "engaged_users", label: "engaged" },
        { icon: Heart,         key: "reactions",     label: "reactions" },
        { icon: MessageCircle, key: "clicks",        label: "clicks" },
      ];

  return (
    <div className="p-2.5 flex gap-3" data-testid={testId}>
      {thumbnail ? (
        <div className="relative shrink-0">
          <img src={thumbnail} alt=""
            className="h-16 w-16 rounded-sm object-cover border border-zinc-200" />
          {isVideo && (
            <PlayCircle className="absolute inset-0 m-auto h-5 w-5 text-white
              drop-shadow-lg" />
          )}
        </div>
      ) : (
        <div className="h-16 w-16 rounded-sm bg-zinc-100 border border-zinc-200
          flex items-center justify-center shrink-0">
          <ImageIcon className="h-5 w-5 text-zinc-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-zinc-700 line-clamp-2 leading-snug">
          {text}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-1.5">
          {stats.filter((s) => post[s.key] != null).map((s) => {
            const Icon = s.icon;
            return (
              <span key={s.key}
                className="inline-flex items-center gap-1 text-[10px]
                  font-mono text-zinc-600">
                <Icon className="h-3 w-3" />
                {Number(post[s.key]).toLocaleString()}
                <span className="text-zinc-400">{s.label}</span>
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-1">
          {date && (
            <span className="text-[9px] font-mono text-zinc-400">
              {String(date).slice(0, 10)}
            </span>
          )}
          {permalink && (
            <a href={permalink} target="_blank" rel="noreferrer"
              className="text-[10px] text-blue-600 hover:underline
                inline-flex items-center gap-0.5">
              open <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function DemoBarList({ title, rows, valueKey, labelKey, icon: Icon, testId }) {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey]) || 0));
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden"
      data-testid={testId}>
      <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50
        flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-600" />}
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-800">
          {title}
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {rows.map((r, i) => {
          const v = Number(r[valueKey]) || 0;
          const pct = (v / max) * 100;
          return (
            <div key={`${String(r[labelKey])}-${i}`} className="text-[11px]">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-zinc-700 truncate max-w-[65%]"
                  title={String(r[labelKey])}>
                  {r[labelKey]}
                </span>
                <span className="font-mono text-zinc-500">{v.toLocaleString()}</span>
              </div>
              <div className="h-1 bg-zinc-100 rounded-sm overflow-hidden">
                <div className="h-full bg-zinc-800" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
